// Shared streaming wire contract (browser + Lambda safe).
// Keep this module free of server-only imports (no aws-sdk, no node-only
// globals at import time) so UI/services and handlers validate identically.
// Mirrors infrastructure/resources/libs/types.ts shapes without editing it;
// if those types change, the parent must reconcile them here.

export const STREAMING_MAX_CHUNK_B64_LEN = 28000;
// Largest raw audio payload accepted BEFORE base64 conversion, so encoders
// reject oversized input without allocating the encoded string first.
// floor(28000 / 4) * 3 = 21000.
export const STREAMING_MAX_CHUNK_BYTES = 21000;
export const STREAMING_SESSION_ID_RE = /^[A-Za-z0-9-]{8,128}$/;
export const STREAMING_B64_RE = /^[A-Za-z0-9+/]+={0,2}$/;
// Optional per-chunk correlation. Bounds keep rows and logs small.
export const STREAMING_MAX_CHUNK_ID_LEN = 128;
// Durations are provider-reported seconds; negatives and non-finite values
// are rejected (never defaulted), and absurd values are capped.
export const STREAMING_MAX_CHUNK_DURATION_S = 3600;
// Upper bound on a live session's age: chunks arriving after this are
// rejected so a leaked `active` row cannot absorb worker capacity forever.
// Settled by recovery (5 min replay) long before this; this is backpressure
// against never-ending streams, not the normal end path.
export const STREAMING_SESSION_MAX_AGE_MS = 4 * 60 * 60 * 1000;

// OCPP-style worker envelope discriminators (libs/types.ts MSG_*).
export const WORKER_MSG_CALL = 2;
export const WORKER_MSG_CALLRESULT = 3;
export const WORKER_MSG_CALLERROR = 4;

// Terminal provider streaming events. These complete orders/sessions and
// must never be confused with plain user events or each other.
export const TERMINAL_WORKER_METHODS = [
  'stream_end',
  'stream_init_result',
  'translation_result',
  'downloader_result',
] as const;
export type TerminalWorkerMethod = (typeof TERMINAL_WORKER_METHODS)[number];

// User-side streaming methods (users websocket).
export const USER_STREAM_METHODS = [
  'StreamStart',
  'StreamData',
  'StreamReconnect',
  'EndStream',
] as const;
export type UserStreamMethod = (typeof USER_STREAM_METHODS)[number];

export interface StreamingChunkParams {
  sessionId: string;
  chunk: string;
  timestamp: number;
  duration: number;
  // Optional monotonic sender sequence for duplicate/out-of-order replay
  // detection. Absent on legacy clients (no replay protection then).
  sequence?: number;
  // Optional opaque sender id for the chunk (logged, never trusted).
  chunkId?: string;
}

export interface StreamStartParams {
  orderId: string;
  sourceLanguage: string;
  targetLanguage: string;
  mode: 'voice' | 'subtitle';
  settings?: { maxLatency?: number; chunkSize?: number };
}

export interface StreamReconnectParams {
  sessionId: string;
}

export interface UserStreamEndParams {
  sessionId: string;
}

export interface WorkerStreamEndParams {
  sessionId: string;
  // Optional: older workers omit it and always mean terminal. Only an
  // explicit false is treated as a non-terminal interim event.
  isFinal?: boolean;
  duration?: number;
}

export interface StreamInitResultParams {
  sessionId: string;
  result: 'success' | 'failure';
}

export type ContractError = { error: string };

function err(error: string): ContractError {
  return { error };
}

export function isSessionId(value: unknown): value is string {
  return typeof value === 'string' && STREAMING_SESSION_ID_RE.test(value);
}

export function isBase64Chunk(value: unknown): value is string {
  if (typeof value !== 'string' || !value) return false;
  if (value.length > STREAMING_MAX_CHUNK_B64_LEN) return false;
  if (value.length % 4 !== 0) return false;
  return STREAMING_B64_RE.test(value);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

// --- User envelope -------------------------------------------------------
// Actual wire shape (preserved callers): JSON { data: { type, method, data } }.
// The UX-01 drift shape { method, params } is rejected explicitly.
export function parseUserEnvelope(body: unknown):
  | { ok: true; type: string; method: string; data: any }
  | { ok: false; error: string } {
  let parsed: any = body;
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return { ok: false, error: 'Invalid JSON envelope' };
    }
  }
  if (!parsed || typeof parsed !== 'object') return { ok: false, error: 'Invalid envelope' };
  if (parsed.method !== undefined && parsed.params !== undefined && parsed.data === undefined) {
    return { ok: false, error: 'Unsupported envelope: expected {data:{type,method,data}}' };
  }
  const inner = parsed.data;
  if (!inner || typeof inner !== 'object') return { ok: false, error: 'Missing data envelope' };
  if (typeof inner.method !== 'string' || !inner.method) return { ok: false, error: 'Missing method' };
  // type is informational ('Message'); accept when present, default otherwise.
  return { ok: true, type: typeof inner.type === 'string' ? inner.type : 'Message', method: inner.method, data: inner.data };
}

export function serializeUserEnvelope(message: { type: string; method: string; data?: any; [k: string]: any }): string {
  return JSON.stringify({ data: message });
}

// --- Chunk conversion (browser safe, no silent drops) ---------------------
function bytesToBase64(bytes: Uint8Array): string {
  let s = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    s += String.fromCharCode(...bytes.subarray(i, i + CHUNK) as unknown as number[]);
  }
  if (typeof btoa !== 'undefined') return btoa(s);
  const buf = (globalThis as any).Buffer;
  if (buf) return buf.from(s, 'binary').toString('base64');
  throw new Error('No base64 encoder available');
}

export function encodeChunkToBase64(input: unknown):
  | { ok: true; base64: string }
  | { ok: false; error: string } {
  try {
    if (typeof input === 'string') {
      // Assume already-base64 chunk; validate rather than double-encode.
      if (!isBase64Chunk(input)) return { ok: false, error: 'Invalid chunk: not base64' };
      return { ok: true, base64: input };
    }
    if (input instanceof Uint8Array) {
      if (input.length === 0) return { ok: false, error: 'Invalid chunk: empty' };
      // Reject oversized raw bytes BEFORE allocating the encoded string.
      if (input.length > STREAMING_MAX_CHUNK_BYTES) return { ok: false, error: 'Invalid chunk: too large' };
      return { ok: true, base64: bytesToBase64(input) };
    }
    if (typeof ArrayBuffer !== 'undefined' && input instanceof ArrayBuffer) {
      if (input.byteLength === 0) return { ok: false, error: 'Invalid chunk: empty' };
      if (input.byteLength > STREAMING_MAX_CHUNK_BYTES) return { ok: false, error: 'Invalid chunk: too large' };
      return { ok: true, base64: bytesToBase64(new Uint8Array(input)) };
    }
    if (typeof (globalThis as any).Blob !== 'undefined' && input instanceof (globalThis as any).Blob) {
      // Blob needs async conversion (FileReader/blob.arrayBuffer()); the
      // caller must convert first. Never emit an empty chunk silently.
      return { ok: false, error: 'Chunk requires async conversion: pass ArrayBuffer or Uint8Array' };
    }
    if (input && typeof input === 'object' && 'chunk' in (input as any)) {
      return encodeChunkToBase64((input as any).chunk);
    }
    return { ok: false, error: 'Invalid chunk: unsupported type' };
  } catch (e: any) {
    return { ok: false, error: e?.message || 'Chunk conversion failed' };
  }
}

export function validateStreamingParams(input: any):
  | { ok: true; value: StreamingChunkParams }
  | { ok: false; error: string } {
  if (!input || typeof input !== 'object') return { ok: false, error: 'Invalid StreamingParams: sessionId and base64 chunk required' };
  const { sessionId, chunk, timestamp, duration, sequence, chunkId } = input;
  if (!isSessionId(sessionId)) return { ok: false, error: 'Invalid StreamingParams: sessionId and base64 chunk required' };
  if (!isBase64Chunk(chunk)) return { ok: false, error: 'Invalid StreamingParams: sessionId and base64 chunk required' };
  // Light base64 sanity without decoding large payloads twice.
  try {
    const buf = (globalThis as any).Buffer;
    if (buf) buf.from(chunk, 'base64');
  } catch {
    return { ok: false, error: 'Invalid chunk: not base64' };
  }
  // Timing is validated, never silently defaulted from invalid input: an
  // explicit negative or non-finite timestamp/duration is rejected. Absent
  // values fall back to server time / zero.
  let ts: number = Date.now();
  if (timestamp !== undefined) {
    if (!isFiniteNumber(timestamp) || timestamp < 0) return { ok: false, error: 'Invalid StreamingParams: timestamp must be a non-negative finite number' };
    ts = timestamp;
  }
  let dur: number = 0;
  if (duration !== undefined) {
    if (!isFiniteNumber(duration) || duration < 0) return { ok: false, error: 'Invalid StreamingParams: duration must be a non-negative finite number' };
    if (duration > STREAMING_MAX_CHUNK_DURATION_S) return { ok: false, error: 'Invalid StreamingParams: duration exceeds maximum' };
    dur = duration;
  }
  let seq: number | undefined;
  if (sequence !== undefined) {
    if (!isFiniteNumber(sequence) || !Number.isInteger(sequence) || sequence < 0) {
      return { ok: false, error: 'Invalid StreamingParams: sequence must be a non-negative integer' };
    }
    seq = sequence;
  }
  let cid: string | undefined;
  if (chunkId !== undefined) {
    if (typeof chunkId !== 'string' || !chunkId || chunkId.length > STREAMING_MAX_CHUNK_ID_LEN) {
      return { ok: false, error: 'Invalid StreamingParams: chunkId must be a short non-empty string' };
    }
    cid = chunkId;
  }
  return {
    ok: true,
    value: {
      sessionId,
      chunk,
      timestamp: ts,
      duration: dur,
      ...(seq !== undefined ? { sequence: seq } : {}),
      ...(cid !== undefined ? { chunkId: cid } : {}),
    },
  };
}

// Builds the exact user-envelope wire bytes for one audio chunk. Never
// emits an empty chunk: conversion/validation failures are returned, not sent.
export function serializeStreamDataMessage(input: {
  sessionId: unknown;
  chunk: unknown;
  timestamp?: unknown;
  duration?: unknown;
  sequence?: unknown;
  chunkId?: unknown;
}):
  | { ok: true; body: string; value: StreamingChunkParams }
  | { ok: false; error: string } {
  const sid = typeof input.sessionId === 'string' ? input.sessionId : '';
  if (!isSessionId(sid)) return { ok: false, error: 'Invalid StreamingParams: sessionId and base64 chunk required' };
  const encoded = encodeChunkToBase64(input.chunk);
  if (!encoded.ok) return encoded;
  const validated = validateStreamingParams({
    sessionId: sid,
    chunk: encoded.base64,
    timestamp: input.timestamp,
    duration: input.duration,
    sequence: input.sequence,
    chunkId: input.chunkId,
  });
  if (!validated.ok) return validated;
  const v = validated.value;
  return {
    ok: true,
    value: v,
    body: serializeUserEnvelope({
      type: 'Message',
      method: 'StreamData',
      data: {
        chunk: v.chunk,
        timestamp: v.timestamp,
        duration: v.duration,
        sessionId: v.sessionId,
        ...(v.sequence !== undefined ? { sequence: v.sequence } : {}),
        ...(v.chunkId !== undefined ? { chunkId: v.chunkId } : {}),
      },
    }),
  };
}

export function serializeStreamReconnectMessage(sessionId: unknown):
  | { ok: true; body: string }
  | { ok: false; error: string } {
  if (!isSessionId(sessionId)) return { ok: false, error: 'Invalid reconnect: sessionId required' };
  return { ok: true, body: serializeUserEnvelope({ type: 'Message', method: 'StreamReconnect', data: { sessionId } }) };
}

export function serializeUserStreamEndMessage(sessionId: unknown):
  | { ok: true; body: string }
  | { ok: false; error: string } {
  if (!isSessionId(sessionId)) return { ok: false, error: 'Invalid stream end: sessionId required' };
  return { ok: true, body: serializeUserEnvelope({ type: 'Message', method: 'EndStream', data: { sessionId } }) };
}

export function validateStreamReconnectParams(input: any):
  | { ok: true; value: StreamReconnectParams }
  | { ok: false; error: string } {
  // Owner is never taken from the body; it comes from the authorizer.
  const sessionId = input?.sessionId;
  if (!isSessionId(sessionId)) return { ok: false, error: 'Invalid reconnect: sessionId required' };
  return { ok: true, value: { sessionId } };
}

export function validateUserStreamEndParams(input: any):
  | { ok: true; value: UserStreamEndParams }
  | { ok: false; error: string } {
  const sessionId = input?.sessionId;
  if (!isSessionId(sessionId)) return { ok: false, error: 'Invalid stream end: sessionId required' };
  return { ok: true, value: { sessionId } };
}

export function validateStreamStartParams(input: any):
  | { ok: true; value: StreamStartParams }
  | { ok: false; error: string } {
  if (!input || typeof input !== 'object') return { ok: false, error: 'Invalid stream start params' };
  const { orderId, sourceLanguage, targetLanguage, mode } = input;
  if (typeof orderId !== 'string' || !orderId) return { ok: false, error: 'Invalid stream start params' };
  if (typeof sourceLanguage !== 'string' || !sourceLanguage) return { ok: false, error: 'Invalid stream start params' };
  if (typeof targetLanguage !== 'string' || !targetLanguage) return { ok: false, error: 'Invalid stream start params' };
  if (mode !== 'voice' && mode !== 'subtitle') return { ok: false, error: 'Invalid stream start params' };
  return { ok: true, value: input as StreamStartParams };
}

// --- Worker envelope ------------------------------------------------------
export function parseWorkerEnvelope(body: unknown):
  | { ok: true; messageType: number; msgId: string; method?: string; params?: any; result?: any }
  | { ok: false; error: string } {
  let parsed: any = body;
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return { ok: false, error: 'Invalid JSON envelope' };
    }
  }
  if (!Array.isArray(parsed)) return { ok: false, error: 'Unknown message!' };
  const [messageTypePart, msgIdPart, ...more] = parsed;
  if (typeof messageTypePart !== 'number') return { ok: false, error: 'Message type must be a number' };
  if (![WORKER_MSG_CALL, WORKER_MSG_CALLRESULT, WORKER_MSG_CALLERROR].includes(messageTypePart)) {
    return { ok: false, error: 'Unrecognised message type' };
  }
  if (typeof msgIdPart !== 'string' || !msgIdPart) return { ok: false, error: 'Message ID must be a string' };
  if (messageTypePart === WORKER_MSG_CALL) {
    const [method, params] = more;
    if (typeof method !== 'string' || !method) return { ok: false, error: 'Method must be a string' };
    return { ok: true, messageType: messageTypePart, msgId: msgIdPart, method, params };
  }
  const [methodOrResult, result] = more;
  // CALLRESULT carries [method, result] for worker results; CALLERROR [code, desc, details].
  return { ok: true, messageType: messageTypePart, msgId: msgIdPart, method: typeof methodOrResult === 'string' ? methodOrResult : undefined, result: result ?? methodOrResult };
}

export function isTerminalWorkerMethod(method: unknown): method is TerminalWorkerMethod {
  return typeof method === 'string' && (TERMINAL_WORKER_METHODS as readonly string[]).includes(method);
}

export function validateWorkerStreamEndParams(input: any):
  | { ok: true; value: WorkerStreamEndParams }
  | { ok: false; error: string } {
  if (!input || typeof input !== 'object') return { ok: false, error: 'Invalid stream_end params' };
  if (!isSessionId(input.sessionId)) return { ok: false, error: 'Invalid stream_end params' };
  if (input.isFinal !== undefined && typeof input.isFinal !== 'boolean') return { ok: false, error: 'Invalid stream_end params' };
  if (input.duration !== undefined && !isFiniteNumber(input.duration)) return { ok: false, error: 'Invalid stream_end params' };
  return { ok: true, value: { sessionId: input.sessionId, isFinal: input.isFinal, duration: input.duration } };
}

export function validateStreamInitResultParams(input: any):
  | { ok: true; value: StreamInitResultParams }
  | { ok: false; error: string } {
  if (!input || typeof input !== 'object') return { ok: false, error: 'Invalid stream_init_result params' };
  if (!isSessionId(input.sessionId)) return { ok: false, error: 'Invalid stream_init_result params' };
  if (input.result !== 'success' && input.result !== 'failure') return { ok: false, error: 'Invalid stream_init_result params' };
  return { ok: true, value: { sessionId: input.sessionId, result: input.result } };
}

// --- Lifecycle ------------------------------------------------------------
// Durable session phases. Only active accepts chunks. stop_requested means
// the user asked to end and a worker stop was dispatched; finalizing means a
// worker final is applying durable order effects; worker_disconnected means
// the bound worker socket dropped with the order uncommitted (recoverable).
// ended/failed/completed are terminal.
export const SESSION_STATUS_ACTIVE = 'active' as const;
export const SESSION_STATUS_STOP_REQUESTED = 'stop_requested' as const;
export const SESSION_STATUS_FINALIZING = 'finalizing' as const;
export const SESSION_STATUS_WORKER_DISCONNECTED = 'worker_disconnected' as const;
export const SESSION_STATUS_ENDED = 'ended' as const;
export const SESSION_STATUS_FAILED = 'failed' as const;

export type SessionStatus = 'active' | 'ended' | 'failed' | string;

export function isActiveSession(item: any): boolean {
  return !!item && item.status === 'active';
}

export function isTerminalSessionStatus(status: unknown): boolean {
  return status === 'ended' || status === 'failed' || status === 'completed';
}

// Non-terminal but not accepting new work: user stop dispatched, worker
// final in flight, or worker socket lost with recovery pending.
export function isSettlingSessionStatus(status: unknown): boolean {
  return (
    status === SESSION_STATUS_STOP_REQUESTED ||
    status === SESSION_STATUS_FINALIZING ||
    status === SESSION_STATUS_WORKER_DISCONNECTED
  );
}

export function sessionKey(sessionId: string, ownerUserId: string): { PK: string; SK: string } {
  return { PK: `SESSION#${sessionId}`, SK: `USER#${ownerUserId}` };
}

// Worker-binding GSI conventions for the streaming-sessions table (GSI1/GSI2
// exist on the table but older rows never wrote them; new rows do, and the
// disconnect sweep queries them with a checkpointed scan fallback).
export function sessionWorkerGsiPk(workerClientId: string): string {
  return `WORKER#${workerClientId}`;
}

export function sessionUserGsiPk(ownerUserId: string): string {
  return `USER#${ownerUserId}`;
}

// Immutable worker binding written at authorized session creation, before
// any init is dispatched. Init/final conditions require this binding plus a
// matching authorizer identity; a row is never bound from an inbound worker
// message alone. Legacy rows predate translatorClientId and carry the
// expected worker in `clientId`.
export function sessionExpectedWorker(item: any): string | undefined {
  return item?.translatorClientId || item?.clientId || undefined;
}

export function sessionWorkerSocket(item: any): string | undefined {
  return item?.translatorConnectionId || item?.translatorId || item?.connectionId || undefined;
}

// Worker result statuses that may flow into order/output records. Anything
// else (e.g. free-form provider strings) is rejected before any write, so a
// worker response can never set an arbitrary order status.
export const WORKER_RESULT_STATUSES = [
  'succeeded',
  'completed',
  'failed',
  'canceled',
  'cancelled',
] as const;

export function isWorkerResultStatus(value: unknown): value is (typeof WORKER_RESULT_STATUSES)[number] {
  return typeof value === 'string' && (WORKER_RESULT_STATUSES as readonly string[]).includes(value);
}

// Worker CALLERROR classification: a transport/control error (timeout,
// socket reset, rate limit) is recoverable — the order stays non-terminal
// for the timeout/recovery path — while an explicit terminal code means the
// worker will never deliver and the job must be failed, never stranded.
// Unknown codes are recoverable (fail open toward retry, never toward a
// fabricated terminal state).
export type WorkerCallErrorSeverity = 'recoverable' | 'terminal';
const TERMINAL_CALL_ERROR_RE = /fatal|terminal|failed|failure|reject|invalid|unsupported|exceed|quota/i;
export function classifyWorkerCallError(code: unknown, _desc?: unknown): WorkerCallErrorSeverity {
  if (typeof code !== 'string' || !code) return 'recoverable';
  return TERMINAL_CALL_ERROR_RE.test(code) ? 'terminal' : 'recoverable';
}

// Stable delivery-receipt keyspace (stored in the streaming-sessions table,
// which shares PK/SK shape). Receipts use a disjoint PK namespace
// (`RECEIPT#jobId` / SK `RECEIPT`) so session PK queries (`SESSION#…`),
// the `RECOVERY#…` cursor partition, and GSI1/GSI2 binding queries never
// match them; rows carry no `status`, so settling scans skip them too.
export function deliveryReceiptKey(jobId: string): { PK: string; SK: string } {
  return { PK: `RECEIPT#${jobId}`, SK: 'RECEIPT' };
}

export { err };
